/**
 * Напоминания · под-этап Ф2.6 — логика экрана настроек (⛔ не отправки).
 *
 * Седьмой модуль витрины с настоящей логикой. Отправкой владеет ядро
 * (`core/push.py`): оно решает, кому и когда писать, и берёт счётчик у `runner`.
 * Здесь — только то, что должен уметь ЭКРАН:
 *   • понять, включено ли напоминание (⚠ значение приезжает то числом, то строкой:
 *     `db.get_settings` приводит к int лишь ключи с int-дефолтом, а через `PATCH`
 *     значение возвращается строкой — на этом уже обожглись с `audio_autoplay`);
 *   • привести время к канону «ЧЧ:ММ» и не пустить в API мусор (там ждёт тот же
 *     формат: `api._valid_time`);
 *   • собрать подпись «когда придёт» — по ЕГО поясу, а не по нашему.
 *
 * ⛔ Ни одного своего правила о времени: витрина показывает выбранное, а не решает.
 * Поэтому здесь нет ни «утро/вечер», ни «по умолчанию 19:00 нарисуем сами» —
 * дефолт приходит из ядра вместе с настройками.
 *
 * 🆕 Окно 2 запуска (19.08): здесь же живёт СЛОВО ДНЯ — второе касание за день, утром.
 * Отдельного файла не заводим: это та же задача экрана (тумблер + время + честная
 * подпись), а разница только в тексте и в запасном времени. Отправкой владеет
 * `core/word_of_day.py`; ⛔ витрина по-прежнему ничего не шлёт и не планирует.
 */

/** Запасное время — ровно то же, что в `db.DEFAULT_SETTINGS.push_time`. */
export const FALLBACK_TIME = "19:00";

/** Запасное время слова дня — ровно то же, что в `db.DEFAULT_SETTINGS.word_time`.
 *  ⚠ Оно ДРУГОЕ: слово приходит утром, напоминание вечером — иначе два сообщения
 *  падали бы человеку подряд. */
export const WORD_FALLBACK_TIME = "09:00";

/** Настройка «включено» может приехать как 1, "1", 0, "0" или не приехать вовсе. */
export function isOn(value: string | number | undefined | null): boolean {
  if (value === undefined || value === null || value === "") return false;
  return String(value) === "1" || String(value) === "true";
}

/**
 * Время к канону «ЧЧ:ММ» (24 часа). `<input type="time">` отдаёт уже в этом виде,
 * но значение может прийти и из базы, правленой руками. Мусор → null: в API его
 * слать нельзя, там он получит 400.
 */
export function normalizeTime(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(raw.trim());
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isInteger(hh) || !Number.isInteger(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/** Время для показа и для `value` поля ввода: неизвестное значение не ломает экран.
 *  `fallback` нужен потому, что запасных времён теперь два: вечернее у напоминания
 *  и утреннее у слова дня. */
export function timeValue(raw: unknown, fallback: string = FALLBACK_TIME): string {
  return normalizeTime(raw) ?? fallback;
}

/** Пояс человеку показываем как есть (`Europe/Moscow`), но без подчёркиваний. */
export function tzLabel(tz: unknown): string {
  const value = typeof tz === "string" && tz.trim() ? tz.trim() : "";
  return value.replace(/_/g, " ");
}

/**
 * Подпись под тумблером: что именно произойдёт. Три честных факта, все — про
 * поведение ядра, а не про наши намерения: время по его поясу, ничего не придёт,
 * если он уже занимался, и ничего не придёт, если учить нечего.
 */
export function summary(o: { enabled: boolean; time: unknown; tz: unknown }): string {
  if (!o.enabled) return "Напоминаний не будет — приложение молчит.";
  const tz = tzLabel(o.tz);
  const when = `Каждый день в ${timeValue(o.time)}` + (tz ? ` по твоему поясу (${tz})` : "");
  return `${when}. Если ты уже занимался сегодня — напоминание не придёт.`;
}


/**
 * Подпись под тумблером слова дня. Те же правила, что у напоминания: говорим ровно
 * то, что делает ядро, и ни слова сверх.
 *
 * ⭐ Два факта, которых нет у напоминания и которые человеку важны:
 *   • слово ЕЩЁ НЕ НАЧАТО — это знакомство вперёд, а не повторение пройденного;
 *   • занятие сегодня слово дня НЕ отменяет (в отличие от напоминания) — потому что
 *     это не напоминание, а подарок.
 */
export function wordSummary(o: { enabled: boolean; time: unknown; tz: unknown }): string {
  if (!o.enabled) return "Слово дня приходить не будет.";
  const tz = tzLabel(o.tz);
  const when = `Каждое утро в ${timeValue(o.time, WORD_FALLBACK_TIME)}`
    + (tz ? ` по твоему поясу (${tz})` : "");
  return `${when}. Придёт слово, которое ты ещё не начинал, — со звуком и примером. `
    + "Через день-два оно встретится на занятии.";
}
